import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  alpha,
} from '@mui/material';
import Masonry from '@mui/lab/Masonry';
import FilterListIcon from '@mui/icons-material/FilterList';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { smartFeedAPI } from './smartFeedAPI';
import SmartCard from './SmartCard';
// GE016 V2 — explicabilité « Pourquoi cette carte ? » + couleurs de zones.
import WhyDialog, { ZONE_COLORS } from './WhyDialog';
import FilterPanel from './FilterPanel';
import { useSubscription } from './SubscriptionContext';
import { useSelector } from 'react-redux';
import SubscriptionUpgradeModal from './SubscriptionUpgradeModal';
import QuickActions from './QuickActions';
// LAP-733 (GE022): pull `getUserLanguage` from translations so the feed
// follows the i18next active language, not the legacy `user_parameter.Langue`.
import { getTranslation, getUserLanguage } from './translations';
import { useTranslation } from 'react-i18next';
// App mobile (Capacitor) — en natif, la demande de localisation passe par
// l'autorisation SYSTÈME (iOS/Android), pas par notre popup maison.
import { Geolocation } from '@capacitor/geolocation';
import { isNativeApp } from 'utils/mobile';

// Un seul popup de localisation : celui du device (OS en natif, navigateur
// sur le web). Plus de popup interne Life. Le choix est persisté en
// localStorage ('granted' | 'denied') pour survivre aux sessions — le
// navigateur/OS mémorise de son côté la permission réelle.
const LOCATION_CHOICE_STORAGE_KEY = 'lifeSmartFeedLocationChoice';
const WEATHER_CACHE_SESSION_KEY = 'lifeSmartFeedWeatherCache';

const getStoredLocationChoice = () => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(LOCATION_CHOICE_STORAGE_KEY);
  } catch (err) {
    return null;
  }
};

const setStoredLocationChoice = (choice) => {
  if (typeof window === 'undefined') return;
  try {
    if (choice) {
      localStorage.setItem(LOCATION_CHOICE_STORAGE_KEY, choice);
    } else {
      localStorage.removeItem(LOCATION_CHOICE_STORAGE_KEY);
    }
  } catch (err) {
    // stockage indisponible (mode privé) : on retentera au prochain montage
  }
};

const getCachedWeather = () => {
  if (typeof window === 'undefined') return null;

  try {
    const cached = JSON.parse(sessionStorage.getItem(WEATHER_CACHE_SESSION_KEY) || 'null');
    if (!cached?.timestamp || Date.now() - cached.timestamp > 10 * 60 * 1000) {
      return null;
    }
    return cached.weather || null;
  } catch (err) {
    return null;
  }
};

const getPublicationDedupeKey = (publication) => {
  if (publication?.define_publication_id) {
    return `definition:${publication.define_publication_id}`;
  }

  return [
    publication?.domain || '',
    publication?.type || '',
    publication?.title || '',
    publication?.button_target || '',
  ].join('|').toLowerCase();
};

const dedupePublications = (items = []) => {
  const seen = new Set();
  const unique = items.filter((publication) => {
    const key = getPublicationDedupeKey(publication);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  // GE016 V2 — tri « zones d'abord, fraîcheur dans DISCOVER » (miroir du
  // backend _apply_feed_ordering) : priority-first UNIQUEMENT dans les zones
  // d'intervention NOW/WATCH/PROGRESS (spec §7.1) ; dans DISCOVER (cartes
  // éditoriales + legacy V1) c'est la FRAÎCHEUR qui prime, sinon toute
  // nouvelle carte à score modeste était enterrée sous les scores élevés et
  // le fil semblait ne plus rien générer.
  // NB : created_at immuable, jamais datetime_displayed (le snooze pousse
  // datetime_displayed à now+24h et faisait remonter les cartes snoozées
  // au-dessus des vraies nouveautés — piège historique LAP-750/751).
  const sortTs = (p) => {
    const raw = p?.created_at || p?.datetime_displayed;
    return raw ? new Date(raw).getTime() : 0;
  };
  const ZONE_RANK = { NOW: 0, WATCH: 1, PROGRESS: 2 };
  const zoneRank = (p) => (p?.zone in ZONE_RANK ? ZONE_RANK[p.zone] : 3);
  const interventionScore = (p) => (
    zoneRank(p) < 3 && typeof p?.priority_score === 'number' ? p.priority_score : 0
  );
  return unique.sort((a, b) => (
    (zoneRank(a) - zoneRank(b))
    || (interventionScore(b) - interventionScore(a))
    || (sortTs(b) - sortTs(a))
  ));
};

// GE016 V2 — regroupe les cartes par zone d'intervention (spec §8).
// Cartes legacy V1 (zone vide) → DISCOVER, comme le backend /summary/.
const INTERVENTION_ZONES = ['NOW', 'WATCH', 'PROGRESS'];

const groupByZone = (items = []) => {
  const zones = { NOW: [], WATCH: [], PROGRESS: [], DISCOVER: [] };
  items.forEach((publication) => {
    const zone = publication?.zone && zones[publication.zone] ? publication.zone : 'DISCOVER';
    zones[zone].push(publication);
  });
  return zones;
};

const clearWeatherCache = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(WEATHER_CACHE_SESSION_KEY);
};

const SmartFeedDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // LAP-733: subscribe to i18next so the dashboard re-renders when the user
  // switches language via the LanguageSelector.
  const { i18n: i18nInstance } = useTranslation();
  const loadMoreRef = useRef(null);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const hasInitialFeedLoadCompletedRef = useRef(false);
  const handleRefreshRef = useRef(null);
  // Guard against concurrent refresh calls that trigger backend 429s.
  // useState's setRefreshing is async, so we need a synchronous ref check.
  const isRefreshingRef = useRef(false);
  const pullStateRef = useRef({ startY: 0, currentY: 0, isDragging: false, pullDistance: 0 });
  const publicationsRef = useRef([]);
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const siteUser = useSelector((store) => store.user);
  
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  // GE016 V2 — publication dont on affiche l'explication (« Pourquoi cette
  // carte ? »). null = dialog fermé.
  const [whyPublicationId, setWhyPublicationId] = useState(null);
  const [filters, setFilters] = useState({
    domain: 'all',
    type: 'all',
  });
  // LAP-733: weather location strings are now resolved against i18next.
  const initialLanguage = (i18nInstance?.language) || 'fr';
  const [weather, setWeather] = useState(() => {
    const cachedWeather = getCachedWeather();
    if (cachedWeather) return cachedWeather;

    if (getStoredLocationChoice() === 'denied') {
      return {
        temperature: null,
        location: getTranslation('locationDenied', initialLanguage),
        weatherCode: null,
      };
    }

    return {
      temperature: null,
      location: getTranslation('locationLoading', initialLanguage),
      weatherCode: null,
    };
  });
  
  // Pull-to-refresh state
  const [pullToRefresh, setPullToRefresh] = useState({
    isPulling: false,
    pullDistance: 0,
    threshold: 60,
  });
  
  // Pagination state (LimitOffsetPagination)
  // LAP-726 (GE021) — initial batch is 15 cards, next batch triggers at 80%
  // scroll height (handled by the IntersectionObserver further down).
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 15; // Items per page (LAP-726)

  // LAP-726 — "X nouvelles publications" pill: counts cards generated by a
  // refresh while the user has scrolled away from the top of the feed.
  const [newCardsCount, setNewCardsCount] = useState(0);
  const feedScrollRef = useRef(null);

  useEffect(() => {
    publicationsRef.current = publications;
  }, [publications]);

  // LAP-733 (GE022): resolve from i18next first, then user preferences, then
  // legacy fields, then navigator. Defined as a stable reference using the
  // latest i18n language and the live siteUser.
  const resolveLanguage = useCallback(() => getUserLanguage(siteUser), [siteUser]);

  // Convenience getter so existing call sites read naturally.
  const userLanguage = resolveLanguage();
  // Re-resolve whenever i18next.language changes so feed reloads pick it up.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentLang = i18nInstance?.language;

  const loadFeed = useCallback(async (currentOffset = 0, isRefresh = false, append = false) => {
    if (!isMountedRef.current) return;
    
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const language = resolveLanguage();
      let response;

      // LAP-726 (GE021) — `status` argument is kept for backwards compat with
      // older smartFeedAPI signatures but is no longer used by the backend.
      // The feed is a single unified chronological list driven by
      // `was_dismissed=false`.
      response = await smartFeedAPI.getFeed(
        limit,
        currentOffset,
        filters.domain !== 'all' ? filters.domain : null,
        filters.type !== 'all' ? filters.type : null,
        subscription,
        language,
        null
      );
      
      if (!isMountedRef.current) return;
      
      // LimitOffsetPagination response format: { count, next, previous, results }
      if (response && response.results) {
        if (append) {
          // Append new publications to existing ones
          setPublications(prev => {
            const existingIds = new Set(prev.map(pub => pub.id));
            return dedupePublications([
              ...prev,
              ...response.results.filter(pub => !existingIds.has(pub.id)),
            ]);
          });
        } else {
          // Replace publications (initial load or refresh)
          setPublications(dedupePublications(response.results));
        }
        
        // Update pagination state
        setOffset(currentOffset);
        setTotalCount(response.count || response.results.length);
        setHasMore(response.next !== null);
      }
      
      setError(null);
      return response;
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err.message === 'SUBSCRIPTION_REQUIRED') {
        setError(getTranslation('subscriptionRequired', resolveLanguage()));
      } else {
        setError(getTranslation('loadError', resolveLanguage()));
      }
      console.error('Error loading feed:', err);
      return null;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, filters.domain, filters.type, subscription, siteUser, currentLang]);

  // Load more publications (infinite scroll)
  const loadMorePublications = useCallback(() => {
    if (!loadingMore && hasMore && !loading && !refreshing) {
      const nextOffset = offset + limit;
      loadFeed(nextOffset, false, true);
    }
  }, [loadingMore, hasMore, loading, refreshing, offset, limit, loadFeed]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initial feed load - wait for subscription to be ready
  const hasAutoRefreshedRef = useRef(false);
  
  useEffect(() => {
    let isMounted = true;
    
    const initialLoad = async () => {
      // Only load once per component mount
      if (hasAutoRefreshedRef.current) {
        return;
      }
      
      // Wait for subscription to be loaded
      if (subscriptionLoading) {
        return; // Will retry when subscriptionLoading becomes false
      }
      
      if (!isMounted) return;
      
      // Mark as auto-refreshed to prevent multiple calls
      hasAutoRefreshedRef.current = true;
      
      try {
        const response = await loadFeed(0, false, false);
        const hasExistingFeed = response?.results?.length > 0;

        if (!hasExistingFeed && isMounted) {
          // Feed vide : génération bloquante puis affichage.
          setRefreshing(true);
          const language = resolveLanguage();
          await smartFeedAPI.refreshFeed(subscription, language);
          setOffset(0);
          setHasMore(true);
          await loadFeed(0, true, false);
        } else if (hasExistingFeed && isMounted) {
          // Feed déjà peuplé : on l'a affiché immédiatement (rapide, pas de flash),
          // puis on génère EN FOND les cartes pertinentes pas encore présentes et on
          // les fait remonter en haut. Sans ça, le fil se fige une fois rempli
          // (chaque template n'apparaît qu'une fois, puis plus rien). handleRefresh
          // gère déjà le prepend + la déduplication + la dégradation gracieuse si les
          // crédits IA manquent (les cartes déterministes s'ajoutent quand même).
          if (handleRefreshRef.current) {
            await handleRefreshRef.current();
          }
        }
      } catch (err) {
        // Silently handle errors during auto-refresh - just load existing feed
        // Don't show errors to user for auto-refresh failures
        if (isMounted) {
          // For any error (429, network, etc.), just load the existing feed without refresh
          try {
            await loadFeed();
          } catch (loadError) {
            // If loading feed also fails, only then show error
            console.error('Error loading feed after auto-refresh failure:', loadError);
            if (isMounted) {
              setError(getTranslation('loadError', resolveLanguage()));
            }
          }
        }
      } finally {
        if (isMounted) {
          hasInitialFeedLoadCompletedRef.current = true;
          setRefreshing(false);
        }
      }
    };
    
    initialLoad();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionLoading, subscription]); // Run when subscription is loaded or changes

  // Reload when filters or subscription change (but not on initial mount)
  useEffect(() => {
    if (!hasInitialFeedLoadCompletedRef.current) {
      return;
    }

    if (!subscriptionLoading && subscription && isMountedRef.current) {
      setLoading(true);
      loadFeed(0, false, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.domain, filters.type, subscription, subscriptionLoading]);

  // Refetch when user returns to tab (visibility change) without replacing the feed already on screen.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current && !subscriptionLoading) {
        handleRefreshRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [subscriptionLoading]);

  const getCurrentPosition = () => {
    // Natif (Capacitor) : plugin Geolocation → déclenche la demande
    // d'autorisation système iOS/Android au premier appel. Même forme de
    // retour ({ coords: { latitude, longitude } }) que l'API navigateur.
    if (isNativeApp()) {
      return Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 10 * 60 * 1000,
      });
    }

    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('GEOLOCATION_NOT_SUPPORTED'));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 10 * 60 * 1000,
      });
    });
  };

  const fetchJson = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP_${response.status}`);
    }
    return response.json();
  };

  const getLocationName = async (latitude, longitude) => {
    try {
      const locationData = await fetchJson(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=${resolveLanguage()}`
      );

      const city = locationData.address?.city ||
        locationData.address?.town ||
        locationData.address?.village ||
        locationData.address?.municipality ||
        locationData.address?.county ||
        locationData.name;
      const country = locationData.address?.country || '';

      if (city && country) return `${city}, ${country}`;
      if (city) return city;
      if (country) return country;
    } catch (err) {
      console.error('Error fetching location name:', err);
    }

    return getTranslation('locationCurrent', resolveLanguage());
  };

  const getWeatherForCoordinates = async (latitude, longitude, fallbackLocationName = null) => {
    const weatherData = await fetchJson(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&current_weather=true`
    );
    const currentWeather = weatherData.current_weather || {
      temperature: weatherData.current?.temperature_2m,
      weathercode: weatherData.current?.weather_code,
    };

    if (currentWeather.temperature === undefined || currentWeather.weathercode === undefined) {
      throw new Error('WEATHER_DATA_UNAVAILABLE');
    }

    const locationName = fallbackLocationName || await getLocationName(latitude, longitude);

    return {
      temperature: Math.round(currentWeather.temperature),
      location: locationName,
      weatherCode: currentWeather.weathercode,
    };
  };

  const fetchWeather = async () => {
    try {
      const position = await getCurrentPosition();
      if (!isMountedRef.current) return;

      const { latitude, longitude } = position.coords;
      const weatherResult = await getWeatherForCoordinates(latitude, longitude);

      if (!isMountedRef.current) return;
      setStoredLocationChoice('granted');
      sessionStorage.setItem(WEATHER_CACHE_SESSION_KEY, JSON.stringify({
        timestamp: Date.now(),
        weather: weatherResult,
      }));
      setWeather(weatherResult);
    } catch (err) {
      console.error('Error fetching weather:', err);
      if (!isMountedRef.current) return;
      clearWeatherCache();
      const isPermissionDenied =
        err.code === 1 ||
        err.message === 'GEOLOCATION_NOT_SUPPORTED' ||
        // Plugin Capacitor : erreurs de refus formulées en texte
        /denied|permission/i.test(err.message || '');
      // Seul un refus explicite est mémorisé : une erreur transitoire
      // (timeout, réseau) ne doit pas empêcher les prochaines tentatives.
      if (isPermissionDenied) {
        setStoredLocationChoice('denied');
      }
      setWeather({
        temperature: null,
        location: getTranslation(
          isPermissionDenied ? 'locationDenied' : 'locationUnavailable',
          resolveLanguage()
        ),
        weatherCode: null,
      });
    }
  };

  // Relance manuelle (clic sur la zone météo quand rien n'est affiché) :
  // on oublie un éventuel refus mémorisé et on retente — si l'utilisateur a
  // réactivé la localisation dans le navigateur/Réglages, ça repart; sinon
  // le refus est simplement re-mémorisé sans nouveau popup.
  const retryWeather = () => {
    setStoredLocationChoice(null);
    setWeather({
      temperature: null,
      location: getTranslation('locationLoading', resolveLanguage()),
      weatherCode: null,
    });
    fetchWeather();
  };

  // Un seul popup, mémorisé : on ne montre plus de dialog interne Life.
  // Au premier accès, l'appel géoloc déclenche l'unique demande du device
  // (popup OS en natif, popup navigateur sur le web) qui mémorise le choix.
  // Ensuite : 'granted' → on récupère la météo sans rien demander,
  // 'denied' → on n'appelle plus la géoloc et on affiche le libellé refusé.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (getCachedWeather()) return; // météo fraîche déjà affichée

    if (!isNativeApp() && getStoredLocationChoice() === 'denied') {
      return; // refus mémorisé : ne pas re-solliciter le navigateur
    }

    fetchWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Infinite scroll using Intersection Observer.
  //
  // LAP-726 (GE021): "Load next batch when user reaches 80% of current list
  // height". We achieve that by placing the sentinel at the *bottom* of the
  // list and using a rootMargin equal to 20% of the viewport height — i.e.
  // the IntersectionObserver fires once the sentinel is within the last 20%
  // of the viewport, which corresponds to the user having scrolled past 80%
  // of the current list.
  useEffect(() => {
    if (!loadMoreRef.current || !isMountedRef.current) return;

    const triggerMargin = Math.round(window.innerHeight * 0.2);

    const observer = new IntersectionObserver(
      (entries) => {
        if (!isMountedRef.current) return;
        const target = entries[0];
        if (target.isIntersecting && !loadingMore && hasMore && !loading && !refreshing) {
          loadMorePublications();
        }
      },
      {
        root: null,
        rootMargin: `0px 0px ${triggerMargin}px 0px`,
        threshold: 0,
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef && isMountedRef.current) {
      try {
        observer.observe(currentRef);
      } catch (err) {
        console.error('Error observing element:', err);
      }
    }

    return () => {
      if (currentRef && currentRef.parentNode) {
        try {
          observer.unobserve(currentRef);
        } catch (err) {
          // Element might already be removed, ignore
        }
      }
      try {
        observer.disconnect();
      } catch (err) {
        // Observer might already be disconnected, ignore
      }
    };
  }, [loadMorePublications, loadingMore, hasMore, loading, refreshing]);

  // Container ref for pull-to-refresh
  const containerRef = useRef(null);

  // Pull-to-refresh functionality
  useEffect(() => {
    const threshold = 60; // Lower threshold for easier triggering

    const checkScrollTop = () => {
      // Check window scroll position
      const windowScroll = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      // Also check if document body can scroll
      const canScroll = document.documentElement.scrollHeight > window.innerHeight;
      // If content doesn't fill viewport, always allow pull-to-refresh
      if (!canScroll) {
        return true;
      }
      // If content can scroll, check if we're at the top (with tolerance)
      const isAtTop = windowScroll <= 20; // Increased tolerance
      console.log('checkScrollTop:', { windowScroll, canScroll, isAtTop });
      return isAtTop;
    };

    const handleTouchStart = (e) => {
      const atTop = checkScrollTop();
      console.log('TouchStart:', { atTop, refreshing, touches: e.touches.length });
      
      // Only trigger if at the top of the page and not already refreshing
      if (atTop && !refreshing) {
        pullStateRef.current.startY = e.touches[0].clientY;
        pullStateRef.current.isDragging = true;
        pullStateRef.current.pullDistance = 0;
        console.log('✅ Pull-to-refresh: Touch started at top, startY:', pullStateRef.current.startY);
      } else {
        console.log('❌ Pull-to-refresh: Not starting - atTop:', atTop, 'refreshing:', refreshing);
      }
    };

    const handleTouchMove = (e) => {
      const state = pullStateRef.current;
      
      if (!state.isDragging) {
        return;
      }
      
      // Check if we're still at the top (allow some tolerance)
      const stillAtTop = checkScrollTop();
      
      if (!stillAtTop) {
        // User scrolled away from top, cancel pull
        console.log('❌ Scrolled away from top, canceling pull');
        state.isDragging = false;
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
        return;
      }

      state.currentY = e.touches[0].clientY;
      state.pullDistance = state.currentY - state.startY;

      console.log('TouchMove:', { 
        currentY: state.currentY, 
        startY: state.startY, 
        pullDistance: state.pullDistance,
        threshold 
      });

      if (state.pullDistance > 0) {
        e.preventDefault();
        e.stopPropagation();
        setPullToRefresh(prev => ({
          ...prev,
          isPulling: true,
          pullDistance: Math.min(state.pullDistance, threshold * 1.5),
        }));
      } else {
        // Pulling up, not down - reset
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
      }
    };

    const handleTouchEnd = () => {
      const state = pullStateRef.current;
      if (!state.isDragging) {
        console.log('TouchEnd: Not dragging, ignoring');
        return;
      }

      console.log('✅ Pull-to-refresh: Touch end - distance:', state.pullDistance, 'threshold:', threshold, 'handleRefreshRef exists:', !!handleRefreshRef.current);

      if (state.pullDistance >= threshold) {
        // Trigger refresh using ref to get latest version
        console.log('🚀 Pull-to-refresh: Triggering refresh! Distance:', state.pullDistance);
        if (handleRefreshRef.current) {
          handleRefreshRef.current();
        } else {
          console.error('❌ handleRefreshRef.current is null! Cannot refresh.');
        }
      } else {
        console.log('⚠️ Pull distance not enough:', state.pullDistance, '<', threshold);
      }

      // Reset pull state
      setPullToRefresh({
        isPulling: false,
        pullDistance: 0,
        threshold: 60,
      });
      
      state.isDragging = false;
      state.pullDistance = 0;
    };

    // Mouse events for desktop testing
    const handleMouseDown = (e) => {
      const atTop = checkScrollTop();
      console.log('MouseDown:', { atTop, refreshing, button: e.button });
      
      if (atTop && e.button === 0 && !refreshing) {
        pullStateRef.current.startY = e.clientY;
        pullStateRef.current.isDragging = true;
        pullStateRef.current.pullDistance = 0;
        console.log('✅ Pull-to-refresh: Mouse down at top, startY:', pullStateRef.current.startY);
      }
    };

    const handleMouseMove = (e) => {
      const state = pullStateRef.current;
      
      if (!state.isDragging) return;
      
      // Check if we're still at the top
      const stillAtTop = checkScrollTop();
      
      if (!stillAtTop) {
        // User scrolled away from top, cancel pull
        console.log(' Mouse moved away from top, canceling pull');
        state.isDragging = false;
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 50,
        });
        return;
      }

      state.currentY = e.clientY;
      state.pullDistance = state.currentY - state.startY;

      console.log('MouseMove:', { 
        currentY: state.currentY, 
        startY: state.startY, 
        pullDistance: state.pullDistance,
        threshold 
      });

      if (state.pullDistance > 0) {
        setPullToRefresh(prev => ({
          ...prev,
          isPulling: true,
          pullDistance: Math.min(state.pullDistance, threshold * 1.5),
        }));
      } else {
        // Pulling up, not down - reset
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
      }
    };

    const handleMouseUp = () => {
      const state = pullStateRef.current;
      if (!state.isDragging) {
        console.log('MouseUp: Not dragging, ignoring');
        return;
      }

      console.log(' Pull-to-refresh: Mouse up - distance:', state.pullDistance, 'threshold:', threshold, 'handleRefreshRef exists:', !!handleRefreshRef.current);

      if (state.pullDistance >= threshold) {
        console.log(' Pull-to-refresh: Triggering refresh (mouse)! Distance:', state.pullDistance);
        if (handleRefreshRef.current) {
          handleRefreshRef.current();
        } else {
          console.error(' handleRefreshRef.current is null! Cannot refresh.');
        }
      } else {
        console.log(' Pull distance not enough:', state.pullDistance, '<', threshold);
      }

      setPullToRefresh({
        isPulling: false,
        pullDistance: 0,
        threshold: 60,
      });
      
      state.isDragging = false;
      state.pullDistance = 0;
    };

    // Also listen for scroll events to enable pull-to-refresh when user scrolls to top
    const handleScroll = () => {
      const state = pullStateRef.current;
      if (!checkScrollTop() && state.isDragging) {
        state.isDragging = false;
        setPullToRefresh({
          isPulling: false,
          pullDistance: 0,
          threshold: 60,
        });
      }
    };

    // Attach events to document body for better capture
    const documentBody = document.body;
    
    console.log('📌 Setting up pull-to-refresh event listeners');
    
    documentBody.addEventListener('touchstart', handleTouchStart, { passive: false });
    documentBody.addEventListener('touchmove', handleTouchMove, { passive: false });
    documentBody.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('mousedown', handleMouseDown, { passive: true });
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mouseup', handleMouseUp, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      documentBody.removeEventListener('touchstart', handleTouchStart);
      documentBody.removeEventListener('touchmove', handleTouchMove);
      documentBody.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Listeners attached once - handleRefresh accessed via handleRefreshRef

  const handleRefresh = useCallback(async () => {
    console.log('🔄 handleRefresh called');
    if (!isMountedRef.current) return;
    // Block concurrent invocations: pull-to-refresh handlers can fire several
    // times in rapid succession (touchend + mouseup + scroll), which spams
    // /refresh/ and trips the backend 429 rate-limiter.
    if (isRefreshingRef.current) {
      console.log('⏭️ Refresh already in progress, skipping');
      return;
    }
    isRefreshingRef.current = true;

    try {
      setRefreshing(true);
      const language = resolveLanguage();
      console.log('🔄 Calling refreshFeed API...');
      const response = await smartFeedAPI.refreshFeed(subscription, language);
      console.log('✅ Refresh API response:', response);
      
      if (!isMountedRef.current) return;
      
      setHasMore(true);
      const newPublications = response?.publications || [];
      if (newPublications.length > 0) {
        const existingIds = new Set(publicationsRef.current.map(pub => pub.id));
        const uniqueNewPublications = newPublications.filter(pub => !existingIds.has(pub.id));
        setPublications(prev => {
          const currentIds = new Set(prev.map(pub => pub.id));
          return [
            ...uniqueNewPublications.filter(pub => !currentIds.has(pub.id)),
            ...prev,
          ];
        });
        setTotalCount(prev => prev + uniqueNewPublications.length);
        setOffset(prev => prev + uniqueNewPublications.length);

        // LAP-726 (GE021) — if the user is not at the top, surface the "X
        // nouvelles publications" pill so they can jump to the new cards.
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        if (scrollY > 200) {
          setNewCardsCount(prev => prev + uniqueNewPublications.length);
        }
      } else {
        await loadFeed(0, true, false);
      }
      setRefreshing(false);
    } catch (err) {
      console.error('❌ Error refreshing feed:', err);
      if (!isMountedRef.current) return;
      if (err.message === 'SUBSCRIPTION_REQUIRED') {
        setError(getTranslation('subscriptionRequired', resolveLanguage()));
      } else if (err.message === 'Failed to fetch' || err.code === 'ERR_NETWORK' || err.message === 'NETWORK_ERROR') {
        setError(getTranslation('networkError', resolveLanguage()));
      } else if (err.response?.status === 429) {
        // Backend rate-limiter hit. Show a dedicated message so the user
        // understands the refresh failed because of throttling, not a bug.
        setError(getTranslation('rateLimitError', resolveLanguage()));
      } else {
        setError(getTranslation('refreshError', resolveLanguage()));
      }
      setRefreshing(false);
    } finally {
      // Always release the lock, even if the request failed.
      isRefreshingRef.current = false;
    }
  }, [subscription, loadFeed, resolveLanguage]);

  // Update handleRefresh ref when handleRefresh changes
  useEffect(() => {
    handleRefreshRef.current = handleRefresh;
  }, [handleRefresh]);

  const handleInteraction = async (publicationId, action) => {
    // LAP-726 (GE021) — Dismiss must feel instant. We remove the card
    // optimistically before the API call resolves and re-insert it on error
    // so the user never sees a half-second delay tap → fade-out.
    let snapshot = null;
    if (action === 'dismiss' || action === 'snooze') {
      snapshot = publicationsRef.current;
      setPublications(prev => prev.filter(pub => pub.id !== publicationId));
    }

    try {
      const response = await smartFeedAPI.interact(publicationId, action);

      if (response.success) {
        // Dismiss / snooze already removed optimistically — nothing else to do.
        if (action === 'dismiss' || action === 'snooze') {
          return;
        }
        // Other actions (like, save, click, ...) — update the card in place.
        setPublications(prev =>
          prev.map(pub =>
            pub.id === publicationId ? response.publication : pub
          )
        );
      }
    } catch (err) {
      console.error('Error handling interaction:', err);
      // LAP-726 — restore the optimistic dismiss/snooze on failure so the
      // card doesn't silently vanish if the API call fails.
      if (snapshot) {
        setPublications(snapshot);
      }
      setError(getTranslation('refreshError', resolveLanguage()));
    }
  };

  const getWeatherIcon = (weatherCode) => {
    // WMO Weather interpretation codes (WW)
    // https://open-meteo.com/en/docs
    if (weatherCode === null) return '🌡️';
    if (weatherCode === 0) return '☀️'; // Clear sky
    if (weatherCode >= 1 && weatherCode <= 3) return '⛅'; // Mainly clear, partly cloudy, and overcast
    if (weatherCode >= 45 && weatherCode <= 48) return '🌫️'; // Fog
    if (weatherCode >= 51 && weatherCode <= 57) return '🌧️'; // Drizzle
    if (weatherCode >= 61 && weatherCode <= 67) return '🌧️'; // Rain
    if (weatherCode >= 71 && weatherCode <= 77) return '❄️'; // Snow
    if (weatherCode >= 80 && weatherCode <= 82) return '🌦️'; // Rain showers
    if (weatherCode >= 85 && weatherCode <= 86) return '🌨️'; // Snow showers
    if (weatherCode >= 95 && weatherCode <= 99) return '⛈️'; // Thunderstorm
    return '🌡️'; // Default
  };

  const filteredPublications = publications.filter(pub => {
    if (filters.domain !== 'all' && pub.domain !== filters.domain) return false;
    if (filters.type !== 'all' && pub.type !== filters.type) return false;
    return true;
  });

  // GE016 V2 — regroupement par zone. Le backend ordonne par -priority_score :
  // les interventions (NOW/WATCH/PROGRESS, plafonnées à 3+5+3) arrivent donc
  // toutes dans la première page — le regroupement local est fiable.
  const zonedPublications = groupByZone(filteredPublications);
  const hasInterventions = INTERVENTION_ZONES.some(
    (zone) => zonedPublications[zone].length > 0
  );
  // « Rien d'important à signaler » : NOW vide est un résultat valide et
  // souhaitable (spec §8) — on le célèbre au lieu de le masquer.
  const showAllClear = !loading && !refreshing && zonedPublications.NOW.length === 0
    && filters.domain === 'all' && filters.type === 'all';

  // Rendu d'une grille de cartes (mutualisé entre les sections de zone).
  // Les cartes V2 (zone renseignée) portent l'affordance « Pourquoi cette
  // carte ? » (spec §13) sous la carte.
  const renderCardsGrid = (items) => (
    <Masonry
      columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
      spacing={2}
      sx={{ alignContent: 'flex-start' }}
    >
      {items.map((publication) => (
        <Box key={publication.id}>
          <SmartCard
            publication={publication}
            onInteraction={handleInteraction}
            onUpgradeRequest={() => setUpgradeModalOpen(true)}
            showArchivedIndicators={false}
          />
          {publication.zone && (
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setWhyPublicationId(publication.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setWhyPublicationId(publication.id);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
                ml: 1,
                cursor: 'pointer',
                color: 'rgba(255, 255, 255, 0.45)',
                '&:hover': { color: 'rgba(255, 255, 255, 0.8)' },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '0.9rem' }} />
              <Typography variant="caption">
                {getTranslation('whyButton', userLanguage)}
              </Typography>
            </Box>
          )}
        </Box>
      ))}
    </Masonry>
  );

  // En-tête de section de zone (pastille colorée + libellé + compteur).
  const renderZoneHeader = (zone, count) => (
    <Box display="flex" alignItems="center" gap={1} mb={1.5} mt={2}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: ZONE_COLORS[zone],
          boxShadow: `0 0 8px ${alpha(ZONE_COLORS[zone], 0.8)}`,
        }}
      />
      <Typography
        variant="subtitle2"
        sx={{
          color: '#E0E0E0',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 1.5,
          fontSize: '0.75rem',
        }}
      >
        {getTranslation(`zone${zone.charAt(0)}${zone.slice(1).toLowerCase()}`, userLanguage)}
      </Typography>
      <Chip
        label={count}
        size="small"
        sx={{
          height: 18,
          fontSize: '0.65rem',
          bgcolor: alpha(ZONE_COLORS[zone], 0.15),
          color: ZONE_COLORS[zone],
        }}
      />
    </Box>
  );

  if (loading && publications.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        bgcolor="#0b0e1a"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: '100vh',
        bgcolor: '#0b0e1a',
        color: '#E0E0E0',
        fontFamily: '"Inter", "Roboto", sans-serif',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* LAP-726 (GE021) — "X nouvelles publications" pill. Shown when the
            user has scrolled away from the top and a refresh has produced new
            cards. Tapping it scrolls back to the top and resets the count. */}
        {newCardsCount > 0 && (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setNewCardsCount(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setNewCardsCount(0);
              }
            }}
            sx={{
              position: 'fixed',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1400,
              px: 2,
              py: 1,
              borderRadius: 999,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
            }}
          >
            {getTranslation('newPublicationsPill', userLanguage, { count: newCardsCount })}
          </Box>
        )}
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              mb={1}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (weather.temperature === null) {
                  retryWeather();
                }
              }}
              onKeyDown={(event) => {
                if (weather.temperature === null && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  retryWeather();
                }
              }}
              sx={{ cursor: weather.temperature === null ? 'pointer' : 'default' }}
            >
              {/* Weather Icon */}
              <Typography
                variant="h3"
                component="span"
                sx={{
                  fontSize: '2.5rem',
                  lineHeight: 1,
                }}
              >
                {getWeatherIcon(weather.weatherCode)}
              </Typography>
              
              {/* Temperature */}
              {weather.temperature !== null && (
                <Typography
                  variant="h3"
                  component="h1"
                  fontWeight="bold"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {weather.temperature}°C
                </Typography>
              )}
              
              {/* Location */}
              <Typography
                variant="h5"
                component="span"
                sx={{
                  color: '#E0E0E0',
                  fontWeight: 500,
                }}
              >
                {weather.location}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {getTranslation('publicationsCount', userLanguage, {
                  shown: filteredPublications.length,
                  total: totalCount,
                })}
              </Typography>
              {subscription && (
                <Chip
                  label={subscription}
                  size="small"
                  sx={{
                    bgcolor: subscription === 'PREMIUM' 
                      ? alpha('#8b5cf6', 0.2) 
                      : subscription === 'STANDARD'
                      ? alpha('#3b82f6', 0.2)
                      : alpha('#6366f1', 0.2),
                    color: subscription === 'PREMIUM' 
                      ? '#8b5cf6' 
                      : subscription === 'STANDARD'
                      ? '#3b82f6'
                      : '#6366f1',
                    fontSize: '0.7rem',
                    height: '20px',
                  }}
                />
              )}
            </Box>
          </Box>

          <Box display="flex" gap={1} alignItems="center">
            <IconButton
              onClick={() => setFilterOpen(!filterOpen)}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filter Panel - Always render to prevent portal unmount errors */}
        <FilterPanel
          filters={filters}
          onFilterChange={setFilters}
          publications={publications}
          open={filterOpen}
        />

        {/* Pull-to-refresh indicator */}
        {pullToRefresh.isPulling && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: `${Math.min(pullToRefresh.pullDistance, pullToRefresh.threshold * 1.5)}px`,
              bgcolor: 'rgba(102, 126, 234, 0.1)',
              zIndex: 1000,
              transform: `translateY(${pullToRefresh.pullDistance > pullToRefresh.threshold ? 0 : -100}%)`,
              transition: pullToRefresh.pullDistance > pullToRefresh.threshold ? 'none' : 'transform 0.3s',
            }}
          >
            <CircularProgress 
              size={24} 
              sx={{ 
                color: pullToRefresh.pullDistance >= pullToRefresh.threshold ? '#667eea' : '#666',
              }} 
            />
            <Typography variant="body2" sx={{ ml: 1, color: '#E0E0E0' }}>
              {pullToRefresh.pullDistance >= pullToRefresh.threshold
                ? getTranslation('releaseToRefresh', userLanguage)
                : getTranslation('pullToRefresh', userLanguage)}
            </Typography>
          </Box>
        )}

        {/* Loading Indicator */}
        {refreshing && (
          <Box display="flex" justifyContent="center" mb={3}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* GE016 V2 — « Rien d'important à signaler » : NOW vide est un
            résultat valide et souhaitable (spec §8). */}
        {showAllClear && (
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{
              mb: 3,
              px: 2.5,
              py: 2,
              borderRadius: '16px',
              bgcolor: alpha('#22c55e', 0.08),
              border: `1px solid ${alpha('#22c55e', 0.25)}`,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: '#22c55e', fontSize: '1.8rem' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#E0E0E0', fontWeight: 700 }}>
                {getTranslation('allClearTitle', userLanguage)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {getTranslation('allClearSubtitle', userLanguage)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Publications par zone (GE016 V2). Les sections NOW/WATCH/PROGRESS
            n'apparaissent que si elles contiennent des cartes ; DISCOVER
            regroupe l'éditorial + les cartes legacy V1 (zone vide) et garde
            la pagination infinie existante. */}
        <Box sx={{ position: 'relative' }}>
          {INTERVENTION_ZONES.map((zone) => (
            zonedPublications[zone].length > 0 && (
              <Box key={zone}>
                {renderZoneHeader(zone, zonedPublications[zone].length)}
                {renderCardsGrid(zonedPublications[zone])}
              </Box>
            )
          ))}

          {zonedPublications.DISCOVER.length > 0 && (
            <Box>
              {/* L'en-tête DISCOVER n'apparaît que si le fil contient aussi des
                  interventions — sinon on garde l'aspect « fil » historique. */}
              {hasInterventions
                && renderZoneHeader('DISCOVER', zonedPublications.DISCOVER.length)}
              {renderCardsGrid(zonedPublications.DISCOVER)}
            </Box>
          )}
        </Box>

        {/* Intersection Observer Target - Outside Masonry to prevent ref conflicts */}
        {hasMore && (
          <Box
            ref={loadMoreRef}
            sx={{ 
              height: '20px', 
              margin: '20px 0',
              width: '100%',
            }}
          />
        )}

        {/* Loading More Indicator */}
        {loadingMore && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {getTranslation('loadingMore', userLanguage)}
            </Typography>
          </Box>
        )}

        {/* End of List Message */}
        {!hasMore && filteredPublications.length > 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              {getTranslation('feedEnd', userLanguage)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {getTranslation('publicationsShown', userLanguage, {
                count: filteredPublications.length,
              })}
            </Typography>
          </Box>
        )}

        {/* Empty State — masqué quand la bannière « Rien d'important à
            signaler » (GE016 V2) est déjà affichée : le silence est un
            résultat, pas un vide à excuser. */}
        {filteredPublications.length === 0 && !loading && !showAllClear && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {getTranslation('noPublicationsYet', userLanguage)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {getTranslation('pullToRefreshHint', userLanguage)}
            </Typography>
          </Box>
        )}
      </Container>

      {/* Quick Actions - Mobile */}
      {isMobile && (
        <QuickActions currentDomain={filters.domain !== 'all' ? filters.domain : null} />
      )}


      {/* GE016 V2 — dialog « Pourquoi cette carte ? » (spec §13) */}
      <WhyDialog
        publicationId={whyPublicationId}
        open={whyPublicationId !== null}
        onClose={() => setWhyPublicationId(null)}
        lang={userLanguage}
      />

      {/* Subscription Upgrade Modal - Always render to prevent portal unmount errors */}
      <SubscriptionUpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        onUpgrade={(tier) => {
          // Handle upgrade - could navigate to subscription page or call API
          console.log('Upgrading to:', tier);
          setUpgradeModalOpen(false);
          // Navigate to subscription management or call upgrade API
        }}
      />
    </Box>
  );
};

export default SmartFeedDashboard;
